import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  getAccessToken,
} from "@/lib/storeManager";
import {
  notifySdAuthChanged,
  SD_ROLE_STORAGE_KEY,
} from "@sd/lib/sdAuthSession";
import { rebindSecureRole, removeSecureLocalItem } from "@/lib/secureLocalStorage";
import ServiceDeskAPIService from "@sd/services/APIs";
import { getDefaultSdPath, sd, type SdAppRole } from "@sd/lib/sdPaths";

const HANDOFF_SESSION_KEY = "sd_sso_handoff_hash";

type SdRoleName = SdAppRole;

interface HandoffJwt {
  userId?: string;
  user_id?: string;
  sub?: string;
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  exp?: number;
  [key: string]: unknown;
}

function decodeUserParam(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const json = decodeURIComponent(escape(atob(raw)));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function resolveUserId(
  userInfo: Record<string, unknown> | null,
  decoded: HandoffJwt,
  uidParam: string | null
): string | null {
  if (uidParam?.trim()) return uidParam.trim();
  const fromInfo = userInfo?.id ?? userInfo?.user_id;
  if (typeof fromInfo === "string" && fromInfo) return fromInfo;
  if (typeof fromInfo === "number") return String(fromInfo);
  const fromJwt =
    decoded.userId ?? decoded.user_id ?? decoded.id ?? decoded.sub;
  if (typeof fromJwt === "string" && fromJwt) return fromJwt;
  if (typeof fromJwt === "number") return String(fromJwt);
  return null;
}

function readHandoffHash(): string {
  const fromUrl = window.location.hash.replace(/^#/, "");
  if (fromUrl) {
    sessionStorage.setItem(HANDOFF_SESSION_KEY, fromUrl);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`
    );
    return fromUrl;
  }
  return sessionStorage.getItem(HANDOFF_SESSION_KEY) ?? "";
}

function pickSdRole(roles: { role_name?: string }[]): SdRoleName | null {
  const names = roles.map(r => r.role_name);
  if (names.includes("ServiceDesk_Admin")) return "ServiceDesk_Admin";
  if (names.includes("ServiceDesk_SuperAgent")) return "ServiceDesk_SuperAgent";
  if (names.includes("ServiceDesk_Agent")) return "ServiceDesk_Agent";
  if (names.includes("ServiceDesk_User")) return "ServiceDesk_User";
  return null;
}

/**
 * PTS → ServiceDesk SSO entry.
 * Stores tokens in SD secure localStorage, resolves SD role, redirects home.
 */
export default function SdSsoHandoff() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Signing you into ServiceDesk…");

  useEffect(() => {
    let cancelled = false;

    const goHome = (role: SdRoleName | null) => {
      sessionStorage.removeItem(HANDOFF_SESSION_KEY);
      window.location.replace(getDefaultSdPath(role));
    };

    const failToLogin = (reason: string) => {
      if (cancelled) return;
      sessionStorage.removeItem(HANDOFF_SESSION_KEY);
      setMessage(reason);
      navigate(sd("login"), { replace: true });
    };

    const run = async () => {
      const hash = readHandoffHash();
      if (!hash) {
        if (getAccessToken()) {
          goHome(null);
          return;
        }
        failToLogin("Missing session. Please sign in.");
        return;
      }

      const params = new URLSearchParams(hash);
      const access = params.get("access");
      const refresh = params.get("refresh");
      if (!access || !refresh) {
        failToLogin("Missing session. Please sign in.");
        return;
      }

      let decoded: HandoffJwt;
      try {
        decoded = jwtDecode<HandoffJwt>(access);
        if (
          typeof decoded.exp === "number" &&
          decoded.exp * 1000 <= Date.now()
        ) {
          failToLogin("Session expired. Please sign in again.");
          return;
        }
      } catch {
        failToLogin("Invalid session. Please sign in.");
        return;
      }

      setLocalAccessToken(access);
      setLocalRefreshToken(refresh);

      const handedOffUser = decodeUserParam(params.get("user"));
      const userId = resolveUserId(handedOffUser, decoded, params.get("uid"));

      const firstName =
        (typeof handedOffUser?.first_name === "string"
          ? handedOffUser.first_name
          : "") ||
        (typeof decoded.first_name === "string" ? decoded.first_name : "");
      const lastName =
        (typeof handedOffUser?.last_name === "string"
          ? handedOffUser.last_name
          : "") ||
        (typeof decoded.last_name === "string" ? decoded.last_name : "");
      const email =
        (typeof handedOffUser?.email === "string"
          ? handedOffUser.email
          : "") || (typeof decoded.email === "string" ? decoded.email : "");

      setLocalUserInfo({
        ...(handedOffUser ?? {}),
        id: userId ?? undefined,
        first_name: firstName,
        last_name: lastName,
        email,
        full_name:
          (typeof handedOffUser?.full_name === "string"
            ? handedOffUser.full_name
            : "") ||
          `${firstName} ${lastName}`.trim() ||
          email ||
          userId ||
          "",
      });

      let role: SdRoleName | null = null;
      if (userId) {
        try {
          const response =
            await ServiceDeskAPIService.auth.getCurrentRole(userId);
          const roles = response.data?.roles ?? [];
          role = pickSdRole(roles);
          if (role) rebindSecureRole(role);
          else removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
        } catch {
          removeSecureLocalItem(SD_ROLE_STORAGE_KEY);
        }
      }

      notifySdAuthChanged();

      if (cancelled) return;
      goHome(role);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-base font-semibold text-slate-700">{message}</p>
        <p className="mt-2 text-sm text-slate-500">Please wait a moment.</p>
      </div>
    </div>
  );
}
